import React, {
  FC,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { InsightsCardWrapper } from './styles';
import {
  Avatar,
  CircularProgress,
  IconButton as MuiIconButton,
  TextareaAutosize,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FlexDiv } from 'crono-fe-common/components/Layout/FlexDiv';
import { colors } from 'crono-fe-common/theme';
import {
  MainButton,
  RewriteButton,
} from 'crono-fe-common/components/CronoButton';
import {
  checkIfValidLinkedinCompanyUrl,
  checkIfValidLinkedinProfileUrl,
} from 'utils/validationUtils';
import { trimAndCleanHtml, stringAvatarProspect } from 'utils/formatUtils';
import {
  useLinkedinGetCompanyJobs,
  useLinkedinGetContactSocialInteractions,
  useLinkedinGetExtendedCompanyInfo,
  useLinkedinGetInvitationsLimit,
  useLinkedinGetProfileDescription,
  useLinkedinSearchCompanies,
} from 'crono-fe-common/hooks/crono-extension/gateway';
import useCompanyInfoWithFallback from 'hooks/services/findNew/useCompanyInfoWithFallback';
import useProfileViewWithFallback from 'hooks/services/findNew/useProfileViewWithFallback';
import {
  CronoGatewayLinkedinGetContactSocialInteractionsAction,
  LinkedinCompany,
  LinkedinProfileExtendedInfo,
} from 'crono-fe-common/types/crono-extension/linkedin';
import { Account } from 'crono-fe-common/types/account';
import { Prospect } from 'crono-fe-common/types/prospect';
import LinkedinUrlUtils from 'crono-fe-common/utils/LinkedinUrlUtils';
import InsightsAccordion, {
  InsightType,
} from 'crono-fe-common/components/insightsAccordion';
import { ExpandableText } from 'crono-fe-common/components/ExpandableText';

import { ReactComponent as IconLocationPin } from 'crono-fe-common/icons/Icon-Location-Pin.svg';
import { ReactComponent as IconPin } from 'crono-fe-common/icons/Icon-pin.svg';
import { ReactComponent as IconKeyTopics } from 'crono-fe-common/icons/Icon-key-topics.svg';
import { ReactComponent as IconBuilding } from 'crono-fe-common/icons/Icon_building.svg';
import { ReactComponent as LinkedinInvalidUrlIcon } from 'assets/images/Linkedin_Invalid_url.svg';
import LinkedinIcon from 'crono-fe-common/icons/Icon-Linkedin';
import RemoveIcon from 'crono-fe-common/icons/Icon-Remove';
import CheckMarkIcon from 'crono-fe-common/icons/Icon-CheckMark';
import useEditAccount from 'hooks/services/account/useEditAccount';
import { AccountInfoInputs } from 'crono-fe-common/types/DTO/accountInfoInputs';
import SearchIcon from 'crono-fe-common/icons/Icon-Search';
import useEditProspect from 'hooks/services/prospect/useEditProspect';
import { UpdateProspect } from 'crono-fe-common/types/DTO/updateProspect';
import IconRedirect from 'crono-fe-common/icons/Icon-Redirect';
import SearchLinkedin from 'pages/taskBoard/find/preconditionLinkedin/SearchLinkedin';
import {
  BlurBackgroundDivAbsolute,
  BlurBackgroundDivFixed,
} from 'crono-fe-common/components/Layout/BlurBackgroundDiv';
import { ReactComponent as IconMessage } from 'assets/images/Icon_Message.svg';
import CronoCheckbox from 'crono-fe-common/components/CronoCheckbox';
import { RewriteMessageInput } from 'crono-fe-common/types/DTO/rewriteMessage';
import useRewriteMessage from 'hooks/services/assistant/useRewriteMessage';
import { useAnalytics } from 'context/analytics';
import { useConditionalSnackBar } from 'context/snackbar';
import CronoEditor from 'components/CronoEditor/CronoEditor';
import ReactQuill from 'react-quill';
import { useAuth } from 'context/auth';
import { useSafeStrategyOverviewContext } from 'context/strategyDetail';
import {
  CompanyInfoToUse,
  RewriteBaseInput,
} from 'crono-fe-common/types/DTO/rewriteBaseInput';
import { RewriteSequenceInstanceInput } from 'crono-fe-common/types/DTO/rewriteSequenceInstanceInput';
import IconBackArrow from 'crono-fe-common/icons/Icon-Back-Arrow';
import { RevertRewrittenSequenceInstanceInput } from 'crono-fe-common/types/DTO/revertRewrittenSequenceInstanceInput';
import {
  getError,
  getImageFromUrl,
  getRecipientInfo,
  getWorkExperienceShort,
  stringAvatarAccount,
  transformToValidUrl,
} from 'crono-fe-common/utils';
import { Constants } from 'crono-fe-common/constants/constants';
import IconStarsInsights from 'crono-fe-common/icons/Icon-Stars-Insights';
import useGetUserCredits from 'hooks/services/user/useGetUserCredits';
import IconOpportunity from 'crono-fe-common/icons/Icon-Opportunity';
import { useLocation, useNavigate } from 'react-router-dom';
import PATH from 'crono-fe-common/constants/path';
import IconLike from 'crono-fe-common/icons/Icon-Like';
import IconDislike from 'crono-fe-common/icons/Icon-Dislike';
import Draggable from 'react-draggable';
import DragIcon from 'crono-fe-common/icons/DragIcon';
import { createPortal } from 'react-dom';
import {
  BuildingSmIcon,
  BagSmIcon,
  CloseMdIcon,
  IconButton,
  Badge,
  LocationSmIcon,
} from '@crono-one/design-system';

export interface RewriteSubmittedContext {
  rewriteRequest: RewriteMessageInput;
  rewriteResponse: string;
  recipientProfile: string;
}

export type ISocialPresence =
  CronoGatewayLinkedinGetContactSocialInteractionsAction['result'];

interface IProps {
  close: () => void;
  rewriteEnabled?: boolean;
  wholeMessage?: string;
  partToRewrite?: string;
  showSelection?: () => void;
  replaceSelectedText?: (text: string) => void;
  isRewriteResponseHtml?: boolean;
  style?: React.CSSProperties;
  setRewriteSubmittedContext?: React.Dispatch<
    React.SetStateAction<RewriteSubmittedContext | undefined>
  >;
  //To fill variables
  prospect: Prospect | null;
  account: Account | null;
  isDraggable?: boolean;
  title?: string;
}

const InsightsCard: FC<IProps> = ({
  close,
  prospect,
  account,
  rewriteEnabled = false,
  wholeMessage = '',
  partToRewrite = '',
  showSelection = () => {},
  replaceSelectedText = (text: string) => {},
  isRewriteResponseHtml = false,
  setRewriteSubmittedContext,
  style,
  isDraggable = false,
  title = 'Insights',
}) => {
  const analytics = useAnalytics();
  const location = useLocation();
  const prevPathnameRef = useRef<string>(location.pathname);

  const {
    insightsContext: {
      rewriteEnabledMode,
      isRewriteSequenceInstanceInProgress,
      rewriteSequenceInstance,
      revertRewrittenSequenceInstance,
      isRevertInProgress,
    },
  } = useSafeStrategyOverviewContext();

  rewriteEnabled = rewriteEnabled || rewriteEnabledMode !== null;

  const [linkedinUrlProspect, setLinkedinUrlProspect] = useState<string>('');

  const [linkedinUrlAccount, setLinkedinUrlAccount] = useState<string>('');

  // Data is fetched upon expanding each InsightAccordion
  // contact logic ----------------
  const memoizedProspectPublicId = useMemo(() => {
    if (prospect) {
      return LinkedinUrlUtils.getIdFromLinkedinProfileUrl(prospect.linkedin);
    }
  }, [prospect?.linkedin]);

  const memoizedCompanyPublicId = useMemo(() => {
    if (account) {
      return (
        LinkedinUrlUtils.getIdFromLinkedinCompanyUrl(account?.linkedin)[0] ?? ''
      );
    }
  }, [account]);

  const {
    call: getProfileView,
    result: getProfileViewResult,
    isLoading: getProfileViewLoading,
  } = useProfileViewWithFallback();

  const {
    call: getProfileDescription,
    result: getProfileDescriptionResult,
    // isLoading: getProfileDescriptionLoading,
  } = useLinkedinGetProfileDescription();

  const {
    call: getContactSocialInteractions,
    result: getContactSocialInteractionsResult,
    isLoading: getContactSocialInteractionsLoading,
  } = useLinkedinGetContactSocialInteractions();

  const handleGetProfileView = (publicId: string | null) => {
    if (publicId) {
      getProfileView({ publicId: publicId });
    }
  };

  useEffect(() => {
    if (getProfileViewResult?.profile?.profileId) {
      getProfileDescription({
        profileId: getProfileViewResult.profile.profileId,
      });
    }
  }, [getProfileViewResult?.profile?.profileId]);

  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      prevPathnameRef.current = location.pathname;
      close();
    }
  }, [location.pathname, close]);

  const handleGetSocialInteractions = async () => {
    if (memoizedProspectPublicId && prospect) {
      await getContactSocialInteractions({
        publicId: memoizedProspectPublicId,
        url: prospect.linkedin,
        // We fetch all of them and filter on FE side.
        // We do not pass here the selected filter value in order to make it easier to select which entities has to be passed to the rewrite
        activities: true,
        comments: true,
        events: true,
      });
    }
  };

  useEffect(() => {
    if (memoizedProspectPublicId && !getProfileViewResult) {
      handleGetProfileView(memoizedProspectPublicId);
    }
  }, [memoizedProspectPublicId, getProfileViewResult, prospect]);

  const { mutate: updateProspect, isLoading: updateProspectLoading } =
    useEditProspect();

  const [isLinkedinContactUrlInvalid, setIsLinkedinContactUrlInvalid] =
    useState<boolean>(false);

  const handlePatchProspectLinkedinUrl = () => {
    if (prospect && prospect.objectId) {
      if (!checkIfValidLinkedinProfileUrl(linkedinUrlProspect)) {
        setIsLinkedinContactUrlInvalid(true);
        return;
      }
      const prospectPatch: UpdateProspect = {
        prospectId: prospect?.objectId,
      };
      if (linkedinUrlProspect) {
        prospectPatch.linkedin = linkedinUrlProspect;
        updateProspect(prospectPatch);
      }
    }
  };

  // company logic -------------

  const {
    data: updatedAccount,
    mutate: updateAccount,
    isLoading: updateAccountLoading,
  } = useEditAccount();

  const [isLinkedinCompanyUrlInvalid, setIsLinkedinCompanyUrlInvalid] =
    useState<boolean>(false);

  const handlePatchCompanyLinkedinUrl = (
    url?: string,
    numericId?: string | null,
  ) => {
    const finalUrl = (url ?? linkedinUrlAccount)?.trim();
    if (account && account.objectId && finalUrl) {
      if (!checkIfValidLinkedinCompanyUrl(finalUrl)) {
        setIsLinkedinCompanyUrlInvalid(true);
        return;
      }
      const accountPatch: AccountInfoInputs = {
        accountId: account?.objectId,
        linkedin: finalUrl,
        ...(numericId && { linkedinNumericId: numericId }),
      };

      updateAccount(accountPatch);
    }
  };

  const {
    call: getExtendedCompanyInfo,
    result: getExtendedCompanyInfoResult,
    isLoading: getExtendedCompanyInfoLoading,
  } = useLinkedinGetExtendedCompanyInfo();

  const {
    call: getCompanyJobs,
    result: getCompanyJobsResult,
    isLoading: getCompanyJobsLoading,
  } = useLinkedinGetCompanyJobs();

  const handleGetCompanyInfo = async (
    numericId?: string,
    publicId?: string,
  ) => {
    if (numericId || publicId) {
      const realPublicId = LinkedinUrlUtils.isNumeric(publicId ?? null)
        ? undefined
        : publicId;
      await getExtendedCompanyInfo({ numericId, publicId: realPublicId });
    }
  };

  const handleGetCompanyJobs = async () => {
    if (memoizedCompanyPublicId && account) {
      await getCompanyJobs({
        numericId: account?.linkedinNumericId,
        publicId: memoizedCompanyPublicId,
      });
    }
  };

  useEffect(() => {
    if (memoizedCompanyPublicId && !getExtendedCompanyInfoResult) {
      handleGetCompanyInfo(account?.linkedinNumericId, memoizedCompanyPublicId);
    }
  }, [memoizedCompanyPublicId, getExtendedCompanyInfoResult]);

  useEffect(() => {
    if (updatedAccount) {
      handleGetCompanyInfo(
        updatedAccount?.data?.data?.linkedinNumericId,
        memoizedCompanyPublicId,
      );
    }
  }, [updatedAccount, updateAccountLoading]);

  const { user } = useAuth();
  const {
    result: linkedinCompanyInfo,
    call: getLinkedinCompanyInfo,
    isLoading: isLoadingCompanyInfo,
  } = useCompanyInfoWithFallback();

  useEffect(() => {
    if (
      user?.company?.linkedinUrl &&
      !linkedinCompanyInfo &&
      !isLoadingCompanyInfo
    ) {
      void getLinkedinCompanyInfo({ url: user.company.linkedinUrl });
    }
  }, [user]);

  const [additionalInformation, setAdditionalInformation] =
    useState<string>('');
  const [feedbackInputValue, setFeedbackInputValue] = useState<
    string | number | readonly string[] | undefined
  >(undefined);

  const [selectedInsights, setSelectedInsights] = useState({
    profileDescription: false,
    workExperiences: [] as number[],
    keyTopics: false,
    activities: [] as number[],

    companyDescription: false,
    fundingData: false,
    news: [] as number[],
    jobOpenings: [] as number[],
  });

  const {
    mutate: rewriteMessage,
    data: rewriteMessageResult,
    isLoading: isRewriteMessageInProgress,
    error: rewriteMessageError,
  } = useRewriteMessage();

  const [rewriteMessageRequest, setRewriteMessageRequest] =
    useState<null | RewriteMessageInput>(null);
  useEffect(() => {
    if (rewriteMessageRequest) {
      rewriteMessage(rewriteMessageRequest);
    }
  }, [rewriteMessageRequest]);

  const getRewriteBaseInput = () => {
    const baseProspectInsights = getProfileViewResult?.profile;
    const allSocialInteractions = getContactSocialInteractionsResult;

    const selectedProspectInsights: Partial<LinkedinProfileExtendedInfo> = {};
    const selectedProspectSocialInteractions: Partial<ISocialPresence> = {};

    if (selectedInsights.profileDescription) {
      selectedProspectInsights.description =
        getProfileDescriptionResult ?? baseProspectInsights?.description;
    }

    if (selectedInsights.workExperiences.length) {
      selectedProspectInsights.workExperience =
        baseProspectInsights?.workExperience?.filter((_, index) =>
          selectedInsights.workExperiences.includes(index),
        );
    }

    if (selectedInsights.keyTopics) {
      if (!allSocialInteractions) {
        return;
      }
      selectedProspectSocialInteractions.allHashtags =
        allSocialInteractions.allHashtags;
    }

    if (selectedInsights.activities.length) {
      if (!allSocialInteractions) {
        return;
      }
      selectedProspectSocialInteractions.activities =
        allSocialInteractions.activities?.filter((_, index) =>
          selectedInsights.activities.includes(index),
        );
    }

    const allCompanyInsights = getExtendedCompanyInfoResult?.company;
    let selectedCompanyInsights: CompanyInfoToUse | null = null;

    if (allCompanyInsights) {
      selectedCompanyInsights = {};
      if (selectedInsights.companyDescription) {
        selectedCompanyInsights.description = allCompanyInsights?.description;
      }

      if (selectedInsights.news.length) {
        if (!selectedCompanyInsights.linkedinActivities) {
          selectedCompanyInsights.linkedinActivities = [];
        }

        selectedCompanyInsights.linkedinActivities =
          allCompanyInsights?.activities?.activities?.filter((_, index) =>
            selectedInsights.news.includes(index),
          ) ?? [];
      }

      if (selectedInsights.jobOpenings.length) {
        selectedCompanyInsights.jobs =
          getCompanyJobsResult?.jobs?.filter((_, index) =>
            selectedInsights.jobOpenings.includes(index),
          ) ?? [];
      }

      if (selectedInsights.fundingData) {
        selectedCompanyInsights.fundingData = allCompanyInsights?.fundingData;
      }
    }

    const senderCompany = linkedinCompanyInfo?.company;
    if (baseProspectInsights) {
      const rewriteBaseInput: RewriteBaseInput = {
        additionalContext: additionalInformation,
        recipientInfo: getRecipientInfo(
          baseProspectInsights,
          getProfileDescriptionResult,
          allSocialInteractions,
        ),
        recipientInfoToUse: {
          linkedinProfileDescription: selectedProspectInsights.description,
          linkedinHashTags: selectedProspectSocialInteractions?.allHashtags
            ? Object.keys(selectedProspectSocialInteractions?.allHashtags)
            : undefined,
          workExperience: getWorkExperienceShort(
            selectedProspectInsights?.workExperience ?? null,
          ),
          linkedinActivities: selectedProspectSocialInteractions?.activities,
        },
        recipientCompanyInfo: allCompanyInsights,
        senderCompanyInfo: senderCompany,
        recipientCompanyInfoToUse: selectedCompanyInsights,
      };

      return rewriteBaseInput;
    }

    return null;
  };

  const [
    isCustomSignatureRemovedBeforeRewrite,
    setIsCustomSignatureRemovedBeforeRewrite,
  ] = useState<boolean>(false);
  const [customSignature, setCustomSignature] = useState<string>('');
  const removeCustomSignatureFromText = useCallback((text: string): string => {
    if (text.includes(Constants.customSignatureOpenTag)) {
      const index = text.indexOf(Constants.customSignatureOpenTag);
      setCustomSignature(text.substring(index, text.length));
      text = text.substring(0, index);
    }
    return text;
  }, []);
  const onRewriteSubmit = () => {
    showSelection();
    const rewriteBaseInput = getRewriteBaseInput();

    const cleanWholeMessage = removeCustomSignatureFromText(wholeMessage);
    const cleanPartToRewrite = removeCustomSignatureFromText(partToRewrite);

    if (cleanPartToRewrite !== partToRewrite) {
      setIsCustomSignatureRemovedBeforeRewrite(true);
    }

    if (rewriteBaseInput) {
      const rewriteMessageInput: RewriteMessageInput = {
        ...rewriteBaseInput,
        wholeMessage: cleanWholeMessage,
        partToPersonalize: cleanPartToRewrite,
      };

      if (analytics) analytics.track('rewrite-clicked', {});

      setRewriteMessageRequest(rewriteMessageInput);
    }
  };

  const [rewriteResponse, setRewriteResponse] = useState<string | null>(null);

  useEffect(() => {
    if (rewriteMessageResult?.data?.data) {
      setRewriteResponse(rewriteMessageResult.data.data);
    }
  }, [rewriteMessageResult?.data?.data]);

  const [isLikeActive, setIsLikeActive] = useState<boolean>(false);
  const [isDislikeActive, setIsDislikeActive] = useState<boolean>(false);

  const handleLikeRewriteResult = () => {
    if (analytics) {
      if (rewriteEnabledMode === 'sequenceInstance') {
        analytics.track('rewrite-sequence-instance-like', {
          sequenceInstanceId: prospect?.sequenceInstanceId,
        });
      } else {
        analytics.track('rewrite-template-like', {
          rewriteRequest: rewriteMessageRequest,
          rewriteResponse: rewriteResponse,
          recipientProfile: memoizedProspectPublicId,
        });
      }

      setIsLikeActive(true);
      setIsDislikeActive(false);
    }
  };

  const handleDislikeRewriteResult = () => {
    if (analytics) {
      if (rewriteEnabledMode === 'sequenceInstance') {
        analytics.track('rewrite-sequence-instance-dislike', {
          sequenceInstanceId: prospect?.sequenceInstanceId,
        });
      } else {
        analytics.track('rewrite-template-dislike', {
          rewriteRequest: rewriteMessageRequest,
          rewriteResponse: rewriteResponse,
          recipientProfile: memoizedProspectPublicId,
        });
      }
      setIsDislikeActive(true);
      setIsLikeActive(false);
    }
  };

  const [rewriteSequenceInstanceRequest, setRewriteSequenceInstanceRequest] =
    useState<null | RewriteSequenceInstanceInput>(null);

  useEffect(() => {
    if (rewriteSequenceInstanceRequest) {
      rewriteSequenceInstance(rewriteSequenceInstanceRequest);
    }
  }, [rewriteSequenceInstanceRequest]);

  const [maxLinkedinInvitationChars, setMaxLinkedinInvitationChars] =
    useState<number>(Constants.linkedinInvitationMaxChars);

  const { call: getLinkedinInvitationLimits } =
    useLinkedinGetInvitationsLimit();

  useEffect(() => {
    getLinkedinInvitationLimits()
      .then((linkedinInvitationLimits) => {
        setMaxLinkedinInvitationChars(
          linkedinInvitationLimits?.maxMessageLength ??
            Constants.linkedinInvitationMaxChars,
        );
      })
      .catch();
  }, []);

  const onRewriteSequenceInstanceSubmit = () => {
    const rewriteBaseInput = getRewriteBaseInput();

    if (rewriteBaseInput && prospect?.sequenceInstanceId) {
      const rewriteSequenceInstanceInput: RewriteSequenceInstanceInput = {
        ...rewriteBaseInput,
        prospectId: prospect.objectId,
        sequenceInstanceId: prospect.sequenceInstanceId,
        invitationCharsLimit: maxLinkedinInvitationChars,
      };

      analytics?.track('rewrite-sequence-clicked', {});

      setRewriteSequenceInstanceRequest(rewriteSequenceInstanceInput);
    }
  };

  const renderInsightAccordion = (type: InsightType, selectable = false) => {
    switch (type) {
      case InsightType.PROFILE_VIEW:
        return (
          <>
            {(getProfileDescriptionResult ??
              getProfileViewResult?.profile?.description) && (
              <div className="flex items-start gap-2">
                {selectable && (
                  <CronoCheckbox
                    checked={selectedInsights.profileDescription}
                    onClick={() => {
                      setSelectedInsights((prev) => ({
                        ...prev,
                        profileDescription: !prev.profileDescription,
                      }));
                    }}
                  />
                )}
                <ExpandableText
                  text={
                    getProfileDescriptionResult ??
                    getProfileViewResult?.profile?.description
                  }
                  lines={3}
                />
              </div>
            )}
            <InsightsAccordion
              prospect={prospect}
              options={getProfileViewResult?.profile}
              icon={<BuildingSmIcon className="size-4" />}
              type={type}
              collapsedHeight="auto"
              publicId={memoizedProspectPublicId}
              companyPublicId={memoizedCompanyPublicId}
              selectedArray={
                selectable ? selectedInsights.workExperiences : undefined
              }
              setSelectedArray={
                selectable
                  ? (arr: number[]) =>
                      setSelectedInsights((prev) => ({
                        ...prev,
                        workExperiences: arr,
                      }))
                  : undefined
              }
              analytics={analytics}
            />
          </>
        );
      case InsightType.SOCIAL_PRESENCE:
        return (
          <InsightsAccordion
            prospect={prospect}
            options={getContactSocialInteractionsResult?.activities}
            fetchOptionsLoading={getContactSocialInteractionsLoading}
            icon={<IconPin />}
            title="Social presence"
            type={type}
            collapsedHeight="fit-content"
            fetchOptions={handleGetSocialInteractions}
            publicId={memoizedProspectPublicId}
            style={{
              gap: '8px',
              border: `1px solid ${colors.grey444}`,
              borderRadius: `12px`,
            }}
            selectedArray={selectable ? selectedInsights.activities : undefined}
            setSelectedArray={
              selectable
                ? (arr: number[]) =>
                    setSelectedInsights((prev) => ({
                      ...prev,
                      activities: arr,
                    }))
                : undefined
            }
            analytics={analytics}
          />
        );
      case InsightType.KEY_TOPICS:
        return (
          <InsightsAccordion
            prospect={prospect}
            options={
              getContactSocialInteractionsResult?.allHashtags
                ? Object.entries(
                    getContactSocialInteractionsResult?.allHashtags,
                  )
                    .sort((a, b) => Number(b[1]) - Number(a[1]))
                    .slice(0, 7)
                    .map((entry) => entry[0])
                : undefined
            }
            fetchOptionsLoading={getContactSocialInteractionsLoading}
            icon={
              selectable ? (
                <CronoCheckbox
                  checked={selectedInsights.keyTopics}
                  onClick={async () => {
                    if (!getContactSocialInteractionsResult) {
                      await handleGetSocialInteractions();
                    }
                    setSelectedInsights((prev) => ({
                      ...prev,
                      keyTopics: !prev.keyTopics,
                    }));
                  }}
                />
              ) : (
                <IconKeyTopics />
              )
            }
            title="Key topics"
            type={type}
            collapsedHeight="fit-content"
            fetchOptions={handleGetSocialInteractions}
            style={{
              gap: '8px',
              border: `1px solid ${colors.grey444}`,
              borderRadius: `12px`,
            }}
            analytics={analytics}
          />
        );
      case InsightType.JOB_OPENINGS:
        return (
          <InsightsAccordion
            prospect={prospect}
            options={getCompanyJobsResult}
            fetchOptionsLoading={getCompanyJobsLoading}
            icon={<BuildingSmIcon className="size-4" />}
            title="Job openings"
            type={type}
            collapsedHeight="fit-content"
            fetchOptions={handleGetCompanyJobs}
            style={{
              gap: '8px',
              border: `1px solid ${colors.grey444}`,
              borderRadius: `12px`,
            }}
            selectedArray={
              selectable ? selectedInsights.jobOpenings : undefined
            }
            setSelectedArray={
              selectable
                ? (arr: number[]) =>
                    setSelectedInsights((prev) => ({
                      ...prev,
                      jobOpenings: arr,
                    }))
                : undefined
            }
            analytics={analytics}
          />
        );
      case InsightType.COMPANY_NEWS:
        return (
          <InsightsAccordion
            prospect={prospect}
            options={getExtendedCompanyInfoResult?.company}
            fetchOptionsLoading={getExtendedCompanyInfoLoading}
            icon={<IconPin />}
            title="News"
            type={type}
            collapsedHeight="fit-content"
            style={{
              gap: '8px',
              border: `1px solid ${colors.grey444}`,
              borderRadius: `12px`,
            }}
            selectedArray={selectable ? selectedInsights.news : undefined}
            setSelectedArray={
              selectable
                ? (arr: number[]) =>
                    setSelectedInsights((prev) => ({
                      ...prev,
                      news: arr,
                    }))
                : undefined
            }
            analytics={analytics}
          />
        );
      case InsightType.FUNDING_ROUNDS:
        return (
          <InsightsAccordion
            prospect={prospect}
            options={getExtendedCompanyInfoResult?.company?.fundingData}
            fetchOptionsLoading={getExtendedCompanyInfoLoading}
            icon={<IconPin />}
            title="Funding"
            type={type}
            collapsedHeight="fit-content"
            style={{
              gap: '8px',
              border: `1px solid ${colors.grey444}`,
              borderRadius: `12px`,
            }}
            toggleSelectedOption={
              selectable
                ? () => {
                    setSelectedInsights((prev) => ({
                      ...prev,
                      fundingData: !prev.fundingData,
                    }));
                  }
                : undefined
            }
            analytics={analytics}
            isSelected={selectedInsights.fundingData}
          />
        );
    }
  };

  const insightProspectOptions = useMemo(() => {
    return [
      InsightType.PROFILE_VIEW,
      InsightType.SOCIAL_PRESENCE,
      InsightType.KEY_TOPICS,
    ].map((option) => renderInsightAccordion(option, rewriteEnabled));
  }, [
    getProfileViewResult,
    getProfileDescriptionResult,
    getContactSocialInteractionsResult,
    getContactSocialInteractionsLoading,
    selectedInsights,
    rewriteEnabled,
  ]);

  const insightCompanyOptions = useMemo(() => {
    return [
      InsightType.JOB_OPENINGS,
      InsightType.COMPANY_NEWS,
      InsightType.FUNDING_ROUNDS,
    ].map((option) => renderInsightAccordion(option, rewriteEnabled));
  }, [
    getExtendedCompanyInfoResult,
    getCompanyJobsResult,
    getCompanyJobsLoading,
    selectedInsights,
    rewriteEnabled,
  ]);

  const overallExperienceTimeJSX = useMemo(() => {
    const obj = getProfileViewResult?.profile?.overallExperienceTime as
      | {
          years?: number;
          months?: number;
        }
      | undefined;

    const years = obj?.years;
    const months = obj?.months;

    if (years == null) return null;
    let seniority = `${years} ${years === 1 ? 'year' : 'years'}`;
    if (months != null && months >= 7) {
      seniority = `${years + 1} ${years + 1 === 1 ? 'year' : 'years'}`;
    }
    return seniority;
  }, [getProfileViewResult]);

  const getLocation = useMemo(() => {
    let location = getProfileViewResult?.profile?.location;
    if (location?.includes(',')) {
      const temp = location.split(',');

      if (temp[temp.length - 1] !== ' undefined') {
        location = `${temp[temp.length - 2]}, ${temp[temp.length - 1]}`;
      } else {
        location = temp[temp.length - 2];
      }
    }
    return location;
  }, [getProfileViewResult]);

  const {
    call: searchCompanies,
    result: searchCompaniesResult,
    isLoading: isSearchCompaniesLoading,
  } = useLinkedinSearchCompanies();

  const [fetchedCompanies, setFetchedCompanies] = useState<
    LinkedinCompany[] | null
  >(null);

  useEffect(() => {
    if (account && account.name && !memoizedCompanyPublicId) {
      searchCompanies({
        keywords: account.name,
      });
    }
  }, []);

  useEffect(() => {
    if (searchCompaniesResult) {
      setFetchedCompanies(searchCompaniesResult.companies ?? []);
    }
  }, [searchCompaniesResult, isSearchCompaniesLoading]);

  const [showSearchContactModal, setShowSearchContactModal] =
    useState<boolean>(false);

  const [isFeedbackView, setIsFeedbackView] = useState(false);
  const [isFeedbackSent, setIsFeedbackSent] = useState(false);

  const onFeedbackSent = () => {
    setFeedbackInputValue('');
    setIsFeedbackView(false);

    setIsFeedbackSent(true);
    setTimeout(() => {
      setIsFeedbackSent(false);
    }, 2000);
  };
  const sendFeedback = () => {
    if (rewriteEnabledMode === 'sequenceInstance' && analytics) {
      analytics.track('rewrite-feedback-sequence-instance', {
        feedback: feedbackInputValue,
        sequenceInstanceId: prospect?.sequenceInstanceId,
      });

      onFeedbackSent();
    } else if (feedbackInputValue && rewriteResponse && analytics) {
      analytics.track('rewrite-feedback', {
        feedback: feedbackInputValue,
        rewriteRequest: rewriteMessageRequest,
        rewriteResponse: rewriteResponse,
        recipientProfile: memoizedProspectPublicId,
      });

      onFeedbackSent();
    }
  };

  const feedbackJSX = useMemo(() => {
    return (
      <>
        <hr className={'feedback-divider'} />
        <div className={'feedback-container'}>
          <input
            placeholder={'How the result could be improved?'}
            value={feedbackInputValue}
            onInput={(e) => setFeedbackInputValue(e.currentTarget.value)}
          />
          <MainButton disabled={!feedbackInputValue} onClick={sendFeedback}>
            <Typography fontSize={14} lineHeight={18} fontWeight={500}>
              Send
            </Typography>
          </MainButton>
        </div>
      </>
    );
  }, [feedbackInputValue]);

  useConditionalSnackBar([
    {
      condition: isFeedbackSent,
      message: 'Your feedback was sent successfully!',
      severity: 'success',
    },
    {
      condition: !!rewriteMessageError,
      message: getError(rewriteMessageError) ?? 'Error during rewrite process',
      severity: 'error',
    },
  ]);

  const allowToRewrite = useMemo(() => {
    const recipientProfile = getProfileViewResult?.profile;

    if (!recipientProfile && !getProfileViewLoading) {
      return {
        isAllowed: false,
        errorMessage: 'Make sure that contact LinkedIn URL is valid',
      };
    }

    if (!partToRewrite && rewriteEnabledMode !== 'sequenceInstance') {
      return {
        isAllowed: false,
        errorMessage: 'Select the text you want to rewrite',
      };
    }

    const trimmedUserSignature = trimAndCleanHtml(
      user?.emailSettings?.signature ?? '',
    );
    const trimmedWholeMessage = trimAndCleanHtml(wholeMessage);
    if (
      trimmedUserSignature === trimmedWholeMessage &&
      rewriteEnabledMode !== 'sequenceInstance'
    ) {
      return {
        isAllowed: false,
        errorMessage:
          'Please select a template or create a new message to use the Rewrite',
      };
    }

    if (
      selectedInsights.profileDescription ||
      selectedInsights.keyTopics ||
      selectedInsights.workExperiences.length ||
      selectedInsights.activities.length ||
      selectedInsights.jobOpenings.length ||
      selectedInsights.news.length ||
      selectedInsights.companyDescription ||
      selectedInsights.fundingData ||
      additionalInformation
    ) {
      return { isAllowed: true };
    } else {
      return {
        isAllowed: false,
        errorMessage:
          "Please select what insights you'd like to use to Rewrite the message",
      };
    }
  }, [
    selectedInsights,
    partToRewrite,
    rewriteEnabledMode,
    getProfileViewResult,
    additionalInformation,
    user,
    wholeMessage,
  ]);

  const editorRef = useRef<ReactQuill | null>(null);
  // A stable, instance-unique id: a static toolbarId collides when two
  // editors are ever mounted at once, leaving Quill's toolbar container
  // pointed at the wrong (or since-removed) node. useId's colons aren't
  // valid unescaped in a CSS id selector, so strip them.
  const editorInstanceId = useId().replace(/:/g, '');

  const {
    insightsContext: { insightsCardRef, sequenceInstance },
  } = useSafeStrategyOverviewContext();

  const rewriteSequenceInstanceWasPerformed = useMemo(() => {
    if (!sequenceInstance?.tasks) return false;

    const activeTasks = sequenceInstance.tasks.filter(
      (task) => !task.completed,
    );

    return (
      rewriteEnabledMode === 'sequenceInstance' &&
      activeTasks.every((task) => !task.templateId || task.personalizedWithAI)
    );
  }, [sequenceInstance]);

  const numberOfTemplatesToRewrite = useMemo(() => {
    if (!sequenceInstance?.tasks) return 0;

    let templatesToRewrite = 0;
    sequenceInstance.tasks.forEach((task) => {
      if (!task.completed && !task.skipped) {
        if (task.templateId) {
          templatesToRewrite++;
        }
        if (task.strategyStepConditional?.templateId) {
          templatesToRewrite++;
        }
      }
    });

    return templatesToRewrite;
  }, [sequenceInstance]);

  const { data: userCredits } = useGetUserCredits();
  const assistantCreditsLeft = userCredits?.data?.data?.aiLeft ?? 1;
  const enoughCredits = assistantCreditsLeft > 0;
  const navigate = useNavigate();

  const handleBuyMoreCredits = useCallback(() => {
    navigate(PATH.SETTINGS.ADD_ONS);
  }, [navigate]);

  const resetStates = () => {
    setRewriteResponse(null);
    setIsFeedbackView(false);
    setFeedbackInputValue('');
    setIsLikeActive(false);
    setIsDislikeActive(false);
    setIsCustomSignatureRemovedBeforeRewrite(false);
  };

  return (
    <div ref={insightsCardRef}>
      <Draggable bounds="parent" handle=".handle">
        <InsightsCardWrapper style={style}>
          {showSearchContactModal &&
            account &&
            prospect &&
            createPortal(
              <div className="font-sans">
                <BlurBackgroundDivFixed>
                  <SearchLinkedin
                    account={account}
                    prospect={prospect}
                    setOpenModal={setShowSearchContactModal}
                    callOnSuccess={() => {
                      setShowSearchContactModal(false);
                    }}
                  />
                </BlurBackgroundDivFixed>
              </div>,
              document.body,
            )}
          <div className="insight-card-container font-sans">
            <div className="flex items-center p-4 gap-2 max-h-14">
              <IconButton
                variant="light"
                tone="lightGray"
                shape="rounded"
                label="Close insights"
                size="sm"
                icon={CloseMdIcon}
                onClick={() => {
                  resetStates();
                  close();
                }}
              />

              <p className="text-lg font-bold flex-1 text-center">{title}</p>

              {isDraggable && (
                <button
                  className="handle cursor-grab active:cursor-grabbing bg-transparent border-none p-0 hover:bg-neutral-100 rounded-md h-fit grid place-content-center"
                  title="Drag to move"
                >
                  <DragIcon />
                </button>
              )}
            </div>

            <div className="bg-neutral-300 h-px w-full" />
            {getProfileViewLoading || updateProspectLoading ? (
              <FlexDiv>
                <CircularProgress />
              </FlexDiv>
            ) : getProfileViewResult ? (
              <>
                <div
                  style={{
                    height: rewriteEnabled
                      ? rewriteEnabledMode === 'sequenceInstance' &&
                        rewriteSequenceInstanceWasPerformed
                        ? isFeedbackView
                          ? 'calc(100% - 68px - 200px)'
                          : 'calc(100% - 68px - 127px)'
                        : 'calc(100% - 68px - 73px)'
                      : 'calc(100% - 68px - 73px)',
                    overflow: 'hidden auto',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div className="insight-card-body">
                    <div className="insight-prospect-header">
                      <Avatar
                        src={getProfileViewResult?.profile?.pictureUrl ?? ''}
                      >
                        {prospect && stringAvatarProspect(prospect)}
                      </Avatar>
                      <div className="prospect-name-title">
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            fontSize={'14px'}
                            fontWeight={600}
                            lineHeight={'22px'}
                            className="contact-name"
                            minWidth={'fit-content'}
                            noWrap
                          >
                            {prospect?.name}
                          </Typography>
                        </div>
                        <div className="flex gap-2 items-center flex-wrap">
                          <Badge
                            startIcon={BuildingSmIcon}
                            shape="rounded"
                            tone="darkSky"
                            size="lg"
                          >
                            {getProfileViewResult?.profile?.seniorityLevel}:{' '}
                            {overallExperienceTimeJSX}
                          </Badge>

                          {getLocation && (
                            <Badge
                              startIcon={LocationSmIcon}
                              shape="rounded"
                              tone="darkSky"
                              size="lg"
                            >
                              {getLocation}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    {insightProspectOptions}
                  </div>
                  <div className="horizontal-divider" />
                  {getExtendedCompanyInfoLoading ? (
                    <FlexDiv height="50%">
                      <CircularProgress />
                    </FlexDiv>
                  ) : getExtendedCompanyInfoResult ? (
                    <div className="insight-card-body">
                      <div className="insight-company-header">
                        <div
                          style={{
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'center',
                            width: '100%',
                          }}
                        >
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: '32px',
                              height: '32px',
                            }}
                            src={
                              getExtendedCompanyInfoResult?.company?.imageUrl ??
                              (account?.website &&
                                getImageFromUrl(account.website, null))
                            }
                          >
                            {account &&
                              stringAvatarAccount(account?.name || '')}
                          </Avatar>
                          <div className="company-name-industry">
                            <Typography
                              fontSize={'14px'}
                              fontWeight={600}
                              lineHeight={'22px'}
                              className="contact-name"
                              noWrap
                            >
                              {getExtendedCompanyInfoResult?.company?.name}
                            </Typography>
                            <Typography
                              fontSize={'12px'}
                              fontWeight={400}
                              color={colors.grey11}
                              noWrap
                            >
                              {getExtendedCompanyInfoResult?.company?.category}
                            </Typography>
                          </div>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                          }}
                        >
                          <span className="blue-badge">
                            <BagSmIcon className="size-4" />{' '}
                            {getExtendedCompanyInfoResult?.company?.industry}
                          </span>
                          <span className="blue-badge">
                            <IconLocationPin />{' '}
                            {getExtendedCompanyInfoResult?.company?.city}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        {rewriteEnabled && (
                          <CronoCheckbox
                            checked={selectedInsights.companyDescription}
                            onClick={() => {
                              setSelectedInsights((prev) => ({
                                ...prev,
                                companyDescription: !prev.companyDescription,
                              }));
                            }}
                          />
                        )}
                        <ExpandableText
                          text={
                            getExtendedCompanyInfoResult?.company?.description
                          }
                          lines={3}
                        />
                      </div>
                      {insightCompanyOptions}
                    </div>
                  ) : account && account?.name && memoizedCompanyPublicId ? (
                    <div
                      className="insight-card-body"
                      style={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <Typography fontSize={'16px'} fontWeight={600}>
                        Unable to load company insights
                      </Typography>
                      <Typography fontSize={'14px'} color={colors.grey11}>
                        We couldn't load LinkedIn information for this company.
                      </Typography>
                    </div>
                  ) : account && account?.name ? (
                    <div className="insight-card-body">
                      {/*These 2 parts are different depending on the result of the fetch companies */}
                      {isSearchCompaniesLoading || updateAccountLoading ? (
                        <FlexDiv>
                          <CircularProgress />
                        </FlexDiv>
                      ) : fetchedCompanies && fetchedCompanies.length ? (
                        <>
                          <Typography fontSize={'16px'} fontWeight={600}>
                            LinkedIn companies for "{account.name}" :
                          </Typography>
                          <Typography fontSize={'14px'} color={colors.grey11}>
                            Please confirm the company LinkedIn page to gather
                            additional information:
                          </Typography>
                          {fetchedCompanies.map((company, index) => {
                            return (
                              <div key={index} className="confirm-company-card">
                                <Avatar
                                  src={company.logoUrl}
                                  variant="rounded"
                                  sx={{
                                    width: '32px',
                                    height: '32px',
                                  }}
                                />
                                <div
                                  className="company-name-industry"
                                  style={{ gap: '0px' }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      width: '100%',
                                      gap: '10px',
                                    }}
                                  >
                                    <Typography
                                      fontSize={'14px'}
                                      fontWeight={600}
                                      lineHeight={'22px'}
                                      className="contact-name"
                                      noWrap
                                    >
                                      {company.title}
                                    </Typography>
                                    <MuiIconButton
                                      style={{
                                        width: 20,
                                        height: 20,
                                        padding: 1,
                                      }}
                                      onClick={() => {
                                        if (company.url)
                                          window.open(
                                            transformToValidUrl(company.url) ??
                                              undefined,
                                            '_blank',
                                          );
                                      }}
                                    >
                                      <LinkedinIcon
                                        color={colors.intenseBlue}
                                      />
                                    </MuiIconButton>
                                  </div>
                                  <Typography
                                    fontSize={'12px'}
                                    fontWeight={400}
                                    color={colors.grey11}
                                    noWrap
                                  >
                                    {company?.primarySubtitle}
                                  </Typography>
                                </div>
                                <span
                                  className="right-side-button-wrapper remove-icon"
                                  onClick={() => {
                                    setFetchedCompanies((prev) =>
                                      (prev ?? [])?.filter(
                                        (c) =>
                                          c.numericId !== company.numericId,
                                      ),
                                    );
                                  }}
                                >
                                  <RemoveIcon color={colors.inactive} />
                                </span>
                                <span
                                  className="right-side-button-wrapper select-icon"
                                  onClick={() =>
                                    handlePatchCompanyLinkedinUrl(
                                      company.url,
                                      company.numericId,
                                    )
                                  }
                                >
                                  <CheckMarkIcon
                                    color={colors.darkGreen}
                                    strokeWidth={2}
                                  />
                                </span>
                              </div>
                            );
                          })}
                          <Typography fontSize={'14px'} fontWeight={600}>
                            Or insert link manually:
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography fontSize={'16px'} fontWeight={600}>
                            Missing company LinkedIn URL:
                          </Typography>
                          <Typography fontSize={'14px'} color={colors.grey11}>
                            Please insert link manually
                          </Typography>
                        </>
                      )}
                      {updateAccountLoading ? (
                        <FlexDiv height="50%">
                          <CircularProgress />
                        </FlexDiv>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            width: '100%',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              height: '100%',
                              border: `1px solid ${colors.grey444}`,
                              borderRadius: '12px',
                            }}
                          >
                            <TextField
                              name={'company-linkedin'}
                              variant="standard"
                              autoComplete="off"
                              InputProps={{
                                disableUnderline: true,
                              }}
                              fullWidth
                              placeholder="Insert LinkedIn URL"
                              onChange={(ev) => {
                                if (isLinkedinCompanyUrlInvalid) {
                                  setIsLinkedinCompanyUrlInvalid(false);
                                }
                                setLinkedinUrlAccount(ev.target.value);
                              }}
                              value={linkedinUrlAccount}
                              sx={{
                                padding: '6px',
                              }}
                            />
                            {linkedinUrlAccount && (
                              <IconRedirect
                                onClick={() => {
                                  if (linkedinUrlAccount) {
                                    window.open(
                                      transformToValidUrl(linkedinUrlAccount) ??
                                        undefined,
                                      '_blank',
                                    );
                                  }
                                }}
                                className="redirect-icon-linkedin-url"
                              />
                            )}
                            {linkedinUrlAccount && (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  padding: 6,
                                  borderLeft: `1px solid ${colors.grey444}`,
                                }}
                              >
                                <span
                                  className="right-side-button-wrapper remove-icon"
                                  onClick={() => {
                                    setLinkedinUrlAccount('');
                                  }}
                                >
                                  <RemoveIcon color={colors.inactive} />
                                </span>
                                <span
                                  className="right-side-button-wrapper select-icon"
                                  onClick={() => {
                                    handlePatchCompanyLinkedinUrl();
                                  }}
                                >
                                  <CheckMarkIcon
                                    color={colors.darkGreen}
                                    strokeWidth={2}
                                  />
                                </span>
                              </div>
                            )}
                          </div>
                          {isLinkedinCompanyUrlInvalid && (
                            <Typography
                              color={colors.inactive}
                              fontSize={'11px'}
                              lineHeight={'14px'}
                              fontWeight={400}
                            >
                              Linkedin URL is invalid
                            </Typography>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <></>
                  )}
                  {(getExtendedCompanyInfoLoading ||
                    getExtendedCompanyInfoResult ||
                    (account && account?.name)) && (
                    <div className="horizontal-divider" />
                  )}

                  {rewriteEnabled && (
                    <div className={'rewrite-textarea-wrapper'}>
                      <TextareaAutosize
                        value={additionalInformation}
                        onChange={(e) =>
                          setAdditionalInformation(e.target.value)
                        }
                        id="rewrite-textarea"
                        minRows={2}
                        className="rewrite-textarea dark-scrollbar"
                        placeholder="Provide additional information..."
                      />
                    </div>
                  )}
                </div>
                {rewriteEnabled && !rewriteResponse && (
                  <div className="insights-rewrite-section">
                    <div className="rewrite-inner-wrapper">
                      <div className={'rewrite-controls'}>
                        <Tooltip
                          enterDelay={400}
                          enterNextDelay={400}
                          title={allowToRewrite.errorMessage}
                          arrow
                          placement={'top'}
                        >
                          <div>
                            <RewriteButton
                              onClick={
                                rewriteEnabledMode === 'sequenceInstance'
                                  ? onRewriteSequenceInstanceSubmit
                                  : onRewriteSubmit
                              }
                              disabled={
                                !allowToRewrite.isAllowed || !enoughCredits
                              }
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding:
                                  allowToRewrite.isAllowed && enoughCredits
                                    ? '8px 8px 8px 12px'
                                    : undefined,
                                height: 40,
                              }}
                            >
                              <span>
                                <IconStarsInsights
                                  color={
                                    !allowToRewrite.isAllowed || !enoughCredits
                                      ? colors.grey11
                                      : colors.mainDark
                                  }
                                  viewBox="0 0 16 16"
                                />
                              </span>

                              {rewriteEnabledMode === 'sequenceInstance'
                                ? 'Rewrite All'
                                : 'Rewrite'}
                            </RewriteButton>
                          </div>
                        </Tooltip>

                        {!enoughCredits && (
                          <Tooltip
                            title={
                              <span
                                style={{
                                  whiteSpace: 'pre-line',
                                  display: 'flex',
                                  textAlign: 'center',
                                }}
                              >
                                There are no more credits left. <br />
                                Please buy more credits to use rewrite with AI
                                feature.
                              </span>
                            }
                            placement="top"
                            arrow
                          >
                            <div
                              style={{
                                display: 'flex',
                                width: 'fit-content',
                                gap: '4px',
                                borderRadius: '20px',
                                alignItems: 'center',
                                height: '40px',
                                padding: '3px 8px 3px 12px',
                                background: colors.inactiveLight,
                                color: colors.inactiveHover,
                              }}
                            >
                              <IconOpportunity
                                color={colors.inactive}
                                size={16}
                              />
                              <Typography
                                fontSize={11}
                                lineHeight={'14px'}
                                fontWeight={500}
                                color={colors.inactive}
                                whiteSpace={'nowrap'}
                              >
                                0 credits
                              </Typography>
                              <MuiIconButton
                                onClick={handleBuyMoreCredits}
                                className="find-button"
                                sx={{
                                  padding: '2px 4px 2px 6px !important',
                                  backgroundColor: colors.inactive,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px',
                                  width: 56,
                                  borderRadius: '12px',
                                  cursor: 'pointer',
                                  '&.MuiIconButton-root:hover': {
                                    backgroundColor: colors.inactiveHover,
                                  },
                                }}
                              >
                                <Typography
                                  fontSize={'14px'}
                                  color={colors.white}
                                  fontWeight={500}
                                >
                                  Buy
                                </Typography>
                              </MuiIconButton>
                            </div>
                          </Tooltip>
                        )}
                      </div>

                      {rewriteSequenceInstanceWasPerformed && (
                        <div className={'feedbacks-controls'}>
                          <Tooltip
                            enterDelay={400}
                            enterNextDelay={400}
                            title={'Back to original templates'}
                            arrow
                            placement={'top'}
                          >
                            {isRevertInProgress ? (
                              <div className={'revert-button'}>
                                <CircularProgress />
                              </div>
                            ) : (
                              <MuiIconButton
                                className={'revert-button'}
                                onClick={() => {
                                  if (prospect?.sequenceInstanceId) {
                                    const request: RevertRewrittenSequenceInstanceInput =
                                      {
                                        prospectId: prospect.objectId,
                                        sequenceInstanceId:
                                          prospect.sequenceInstanceId,
                                      };

                                    if (analytics)
                                      analytics.track(
                                        'revert-rewritten-sequence',
                                        request,
                                      );

                                    revertRewrittenSequenceInstance(request);
                                  }
                                }}
                              >
                                <IconBackArrow color={colors.black} />
                              </MuiIconButton>
                            )}
                          </Tooltip>

                          <div className={'buttons-container'}>
                            <MuiIconButton
                              className={'feedback-icon'}
                              onClick={() => setIsFeedbackView((prev) => !prev)}
                            >
                              <IconMessage color={colors.black} />
                            </MuiIconButton>
                            <IconLike
                              className={'like-icon'}
                              active={isLikeActive}
                              onClick={handleLikeRewriteResult}
                            />
                            <IconDislike
                              className={'dislike-icon'}
                              active={isDislikeActive}
                              onClick={handleDislikeRewriteResult}
                            />
                          </div>
                        </div>
                      )}

                      {isFeedbackView && feedbackJSX}
                    </div>
                  </div>
                )}
                {(rewriteResponse ||
                  isRewriteMessageInProgress ||
                  isRewriteSequenceInstanceInProgress) && (
                  <BlurBackgroundDivAbsolute
                    style={{
                      width: 'auto',
                      height: 'auto',
                      inset: '12px',
                      borderRadius: '16px',
                    }}
                  >
                    <div className="insights-rewrite-response-modal">
                      {rewriteResponse ? (
                        <div className="response dark-scrollbar">
                          {isRewriteResponseHtml ? (
                            <CronoEditor
                              toolbarId={`insights-editor-toolbar-${editorInstanceId}`}
                              editorRef={editorRef}
                              value={rewriteResponse}
                              imageSupport={true}
                              readOnly={true}
                            />
                          ) : (
                            <Typography fontSize={14} lineHeight={'24px'}>
                              {rewriteResponse?.split('\n')?.map((line) => (
                                <>
                                  {line}
                                  <br />
                                </>
                              ))}
                            </Typography>
                          )}
                        </div>
                      ) : (
                        <FlexDiv>
                          <CircularProgress />
                        </FlexDiv>
                      )}

                      {rewriteEnabledMode !== 'sequenceInstance' && (
                        <div
                          className={'buttons-container'}
                          style={{ width: '100%' }}
                        >
                          <div className={'buttons-container'}>
                            <MuiIconButton
                              className={'feedback-icon'}
                              onClick={() => setIsFeedbackView((prev) => !prev)}
                            >
                              <IconMessage color={colors.black} />
                            </MuiIconButton>
                            <IconLike
                              className={'like-icon'}
                              active={isLikeActive}
                              onClick={handleLikeRewriteResult}
                            />
                            <IconDislike
                              className={'dislike-icon'}
                              active={isDislikeActive}
                              onClick={handleDislikeRewriteResult}
                            />
                          </div>

                          <div
                            className={'buttons-container'}
                            style={{ marginRight: '36px' }}
                          >
                            <MuiIconButton
                              className={'delete-icon'}
                              disabled={isRewriteMessageInProgress}
                              onClick={() => {
                                if (
                                  analytics &&
                                  rewriteMessageRequest &&
                                  rewriteResponse
                                ) {
                                  analytics.track('rewrite-discarded', {
                                    rewriteRequest: rewriteMessageRequest,
                                    rewriteResponse: rewriteResponse,
                                    recipientProfile: memoizedProspectPublicId,
                                  });
                                }
                                resetStates();
                              }}
                            >
                              <RemoveIcon color={colors.inactive} />
                            </MuiIconButton>
                            <MuiIconButton
                              className="confirm-icon"
                              disabled={isRewriteMessageInProgress}
                              onClick={() => {
                                if (
                                  rewriteMessageRequest &&
                                  rewriteResponse &&
                                  memoizedProspectPublicId &&
                                  setRewriteSubmittedContext
                                ) {
                                  setRewriteSubmittedContext({
                                    rewriteResponse,
                                    rewriteRequest: rewriteMessageRequest,
                                    recipientProfile: memoizedProspectPublicId,
                                  });
                                }

                                let newText = rewriteResponse ?? '';
                                if (
                                  isCustomSignatureRemovedBeforeRewrite &&
                                  customSignature
                                ) {
                                  newText += '<br/>' + customSignature;
                                }

                                replaceSelectedText(newText);
                                resetStates();
                              }}
                            >
                              <CheckMarkIcon color={colors.callScheduled} />
                            </MuiIconButton>
                          </div>
                        </div>
                      )}

                      {isFeedbackView && feedbackJSX}
                    </div>
                  </BlurBackgroundDivAbsolute>
                )}
              </>
            ) : prospect?.linkedin &&
              checkIfValidLinkedinProfileUrl(prospect?.linkedin) ? (
              <FlexDiv
                direction="column"
                justifyContent="center"
                alignItems="center"
                gap="20px"
                style={{ paddingBottom: 100 }}
              >
                <LinkedinInvalidUrlIcon />
                <Typography
                  fontSize="16px"
                  fontWeight={600}
                  textAlign="center"
                  lineHeight="24px"
                >
                  Error fetching insights from this Linkedin URL.
                  <br />
                  Please contact our support.
                </Typography>
              </FlexDiv>
            ) : (
              <FlexDiv
                direction="column"
                justifyContent="center"
                alignItems="center"
                gap="20px"
                style={{ paddingBottom: 100 }}
              >
                <LinkedinInvalidUrlIcon />
                <Typography
                  fontSize="16px"
                  fontWeight={600}
                  textAlign="center"
                  lineHeight="24px"
                >
                  The Linkedin URL of this contact is invalid.
                  <br />
                  Please change it and try again.
                </Typography>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    width: '90%',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      border: `1px solid ${colors.grey444}`,
                      borderRadius: '12px',
                      gap: '8px',
                    }}
                  >
                    <TextField
                      name={'prospect-linkedin'}
                      variant="standard"
                      autoComplete="off"
                      InputProps={{
                        disableUnderline: true,
                      }}
                      fullWidth
                      placeholder="Insert LinkedIn URL"
                      onChange={(ev) => {
                        if (isLinkedinContactUrlInvalid) {
                          setIsLinkedinContactUrlInvalid(false);
                        }
                        setLinkedinUrlProspect(ev.target.value);
                      }}
                      value={linkedinUrlProspect}
                      sx={{
                        padding: '6px',
                      }}
                    />
                    {linkedinUrlProspect && (
                      <IconRedirect
                        onClick={() => {
                          if (linkedinUrlProspect) {
                            window.open(
                              transformToValidUrl(linkedinUrlProspect) ??
                                undefined,
                              '_blank',
                            );
                          }
                        }}
                        className="redirect-icon-linkedin-url"
                      />
                    )}
                    {linkedinUrlProspect ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: 6,
                          borderLeft: `1px solid ${colors.grey444}`,
                        }}
                      >
                        <span
                          className="right-side-button-wrapper remove-icon"
                          onClick={() => {
                            setLinkedinUrlProspect('');
                          }}
                        >
                          <RemoveIcon color={colors.inactive} />
                        </span>
                        <span
                          className="right-side-button-wrapper select-icon"
                          onClick={() => {
                            handlePatchProspectLinkedinUrl();
                          }}
                        >
                          <CheckMarkIcon
                            color={colors.darkGreen}
                            strokeWidth={2}
                          />
                        </span>
                      </div>
                    ) : (
                      <div
                        className="search-prospect-url"
                        onClick={() => setShowSearchContactModal(true)}
                      >
                        <SearchIcon color={colors.white} />
                        <Typography fontSize={'14px'} color={colors.white}>
                          Find
                        </Typography>
                      </div>
                    )}
                  </div>
                  {isLinkedinContactUrlInvalid && (
                    <Typography
                      color={colors.inactive}
                      fontSize={'11px'}
                      lineHeight={'14px'}
                      fontWeight={400}
                    >
                      Linkedin URL is invalid
                    </Typography>
                  )}
                </div>
              </FlexDiv>
            )}
          </div>
        </InsightsCardWrapper>
      </Draggable>
    </div>
  );
};

export default InsightsCard;
